import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PosModals from "../../core/modals/pos-modal/posModals";
import PropTypes from "prop-types";
// import axios from "axios";
import { mockPos } from "../../api/mock/pos";
import ImageWithBasePath from "../../core/img/imagewithbasebath";

// Web Apis
import { fetchWebCategories } from "../../api/website-api/web-category";
import {
  fetchWebProducts,
  fetchWebProductById,
} from "../../api/website-api/web-product";
import {
  createWebOrder,
  buildOrderPayload,
} from "../../api/website-api/web-order";

import "./css/pos.css";

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- image URL helpers ---
const isHttpUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
const looksLikeCatalog = (v) =>
  typeof v === "string" && /^\/?(catalog|catelog)\//i.test(v);
const normalizeKey = (s = "") => {
  let v = String(s)
    .trim()
    .replace(/^\/+/, "")
    .replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v) => {
  if (!v) return "";
  if (isHttpUrl(v)) return v;
  if (looksLikeCatalog(v)) {
    const base = (
      process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
    ).replace(/\/+$/, "");
    return `${base}/${normalizeKey(v)}`;
  }
  return v;
};

//Helper to detect "no store" 403 errors
const noStore403 = (err) => {
  const msg = err?.response?.data?.message || err?.message || "";
  const code = err?.response?.status;
  return code === 403 && /no\s*store/i.test(msg);
};

const Pos = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [demo, setDemo] = useState(false);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      const id = String(cat._id || cat.category_id || cat.id);
      map[id] = cat.name;
    });
    return map;
  }, [categories]);

  const [selectedItems, setSelectedItems] = useState([]);
  const [productCache, setProductCache] = useState({});

  const [receivedAmount, setReceivedAmount] = useState("");
  const [payingAmount, setPayingAmount] = useState(0);
  const [change, setChange] = useState(0);
  const [paymentType, setPaymentType] = useState({
    value: "cash",
    label: "Cash",
  });

  // Invoice number for online payments
  const [invoiceNo, setInvoiceNo] = useState("");

  const [paymentReceiver, setPaymentReceiver] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [staffNote, setStaffNote] = useState("");

  // Customer info state for order creation
  const [customerInfo, setCustomerInfo] = useState({
    firstname: "",
    lastname: "",
    email: "",
    telephone: "",
    company: "",
    address_1: "",
    address_2: "",
    city: "Kuwait City",
    postcode: "",
    country_id: 114,
    zone_id: 1789,
    area_id: 153,
  });

  const [lastTransaction, setLastTransaction] = useState(null);
  const [invoiceLang, setInvoiceLang] = useState("en");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labels = {
    en: {
      invoice: "Invoice",
      orderId: "Order ID",
      date: "Date",
      paymentType: "Payment Type",
      cashier: "Payment Receiver",
      items: "Items",
      qty: "Qty",
      price: "Price",
      lineTotal: "Total",
      subtotal: "Subtotal",
      total: "Total",
      received: "Received",
      paying: "Paying",
      change: "Change",
      thankYou: "Thank you for your purchase!",
      invoiceNo: "Invoice No",
    },
    ar: {
      invoice: "فاتورة",
      orderId: "رقم الطلب",
      date: "التاريخ",
      paymentType: "طريقة الدفع",
      cashier: "المستلم",
      items: "العناصر",
      qty: "الكمية",
      price: "السعر",
      lineTotal: "الإجمالي",
      subtotal: "الإجمالي الفرعي",
      total: "الإجمالي",
      received: "المبلغ المستلم",
      paying: "المبلغ المدفوع",
      change: "الباقي",
      thankYou: "شكراً لتسوقكم!",
      invoiceNo: "رقم الفاتورة",
    },
  };

  const nf = (n, lang) =>
    (typeof n === "number" ? n : Number(n || 0)).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    );

  const buildInvoiceHtml = (tx, lang = "en") => {
    const L = labels[lang] || labels.en;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const alignStart = lang === "ar" ? "right" : "left";
    const alignEnd = lang === "ar" ? "left" : "right";

    const subtotal2 = (tx.items || []).reduce(
      (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
      0
    );

    const total =
      typeof tx.totalAmount === "number" ? tx.totalAmount : subtotal2;
    const received = tx.receivedAmount ?? 0;
    const paying = tx.payingAmount ?? total;
    const changeVal = tx.change ?? Math.max(0, received - paying);

    const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US"
    );

    const rows = (tx.items || [])
      .map(
        (it) => `
      <tr>
        <td>${it.name || ""}</td>
        <td style="text-align:center">${nf(it.quantity || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(it.price || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${nf(
          (it.price || 0) * (it.quantity || 0),
          lang
        )}</td>
      </tr>`
      )
      .join("");

    const invoiceNoRow = tx.invoiceNo
      ? `<div><span>${L.invoiceNo}</span><strong>${tx.invoiceNo}</strong></div>`
      : "";

    // helpful filename hint (works in some browsers)
    const suggestedFileName = `Invoice-${tx.orderId || "order"}.pdf`;

    return `
<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${L.invoice} - ${tx.orderId || ""}</title>

<style>
  :root { color-scheme: light; }
  body { font-family: Arial, sans-serif; margin: 0; padding: 12px; background:#f6f6f6; }
  .wrap {
    width: 100%;
    max-width: 860px;   /* ✅ tablet/desktop friendly */
    margin: 0 auto;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 12px;
    padding: 16px;
  }

  h1 { font-size: 18px; margin: 0 0 10px; text-align: center; }

  .meta { font-size: 13px; margin-bottom: 12px; }
  .meta div { display: flex; gap: 8px; justify-content: space-between; padding: 4px 0; }
  .meta span { opacity: .75; }
  .meta strong { text-align: ${alignEnd}; }

  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { padding: 10px 8px; border-bottom: 1px dashed #ddd; vertical-align: top; }
  th { text-align: ${alignStart}; font-weight: bold; background: #f8f8f8; }
  tfoot td { border: 0; padding: 6px 8px; }
  .total-row td { border-top: 2px solid #000; font-weight: bold; padding-top: 10px; }

  .thanks { text-align: center; margin-top: 14px; font-size: 13px; }

  .end { text-align: ${alignEnd}; }
  .start { text-align: ${alignStart}; }

  /* ✅ Top action bar (hidden in print) */
  .actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .btn {
    border: 0;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px;
    cursor: pointer;
  }
  .btn-primary { background: #0d6efd; color: #fff; }
  .btn-light { background: #f0f0f0; color: #111; }
  .hint { font-size: 12px; opacity: .7; text-align: center; margin-top: 4px; }

  /* ✅ Mobile */
  @media (max-width: 480px) {
    body { padding: 10px; }
    .wrap { padding: 12px; border-radius: 10px; }
    table { font-size: 12px; }
    th, td { padding: 7px 5px; }
    h1 { font-size: 16px; }
  }

  /* ✅ Print styles: removes buttons & background */
  @media print {
    body { background: #fff; padding: 0; }
    .wrap { border: 0; border-radius: 0; padding: 0; }
    .actions, .hint { display: none !important; }
  }
</style>
</head>

<body>
  <div class="wrap">
    <div class="actions">
      <button class="btn btn-primary" onclick="doPrint()">
        Print / Save as PDF
      </button>
      <button class="btn btn-light" onclick="goBack()">
        Back
      </button>
    </div>
    <div class="hint">
      Tip: In the print dialog choose <b>Save as PDF</b> to download.
    </div>

    <h1>${L.invoice} / ${lang === "ar" ? "Invoice" : "فاتورة"}</h1>

    <div class="meta">
      <div><span>${L.orderId}</span><strong>${tx.orderId || "-"}</strong></div>
      <div><span>${L.date}</span><strong>${todayStr}</strong></div>
      <div><span>${L.paymentType}</span><strong>${tx.type || "-"}</strong></div>
      ${invoiceNoRow}
      <div><span>${L.cashier}</span><strong>${
      tx.paymentReceiver || "-"
    }</strong></div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="start">${L.items}</th>
          <th style="text-align:center">${L.qty}</th>
          <th class="end">${L.price}</th>
          <th class="end">${L.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4" style="text-align:center">—</td></tr>`}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3" class="end">${L.subtotal}</td>
          <td class="end">${nf(subtotal2, lang)}</td>
        </tr>
        <tr class="total-row">
          <td colspan="3" class="end">${L.total}</td>
          <td class="end">${nf(total, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.received}</td>
          <td class="end">${nf(received, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.paying}</td>
          <td class="end">${nf(paying, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.change}</td>
          <td class="end">${nf(changeVal, lang)}</td>
        </tr>
      </tfoot>
    </table>

    <div class="thanks">${L.thankYou}</div>
  </div>

  <script>
    // Set a title so some browsers use it as PDF filename
    document.title = ${JSON.stringify(suggestedFileName)};

    function doPrint() {
      // Must be user-clicked for mobile reliability
      window.print();
    }

    function goBack() {
      // returns to POS screen
      window.history.back();
    }
  </script>
</body>
</html>
`;
  };

  const printInvoice = (lang = invoiceLang, tx = lastTransaction) => {
    if (!tx) {
      alert(
        window.translate(
          "No completed transaction to print. Please place an order first."
        )
      );
      return;
    }

    const html = buildInvoiceHtml(tx, lang);

    // ✅ Best for mobile/tablet: open in SAME TAB (no popup blockers)
    const w = window.open("", "_self");
    if (!w) {
      alert(window.translate("Unable to open invoice."));
      return;
    }

    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Fetch the categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoryData = await fetchWebCategories();

        const normalized = categoryData.map((cat) => {
          const raw = cat.image || cat.Image || "category-default.svg";

          const isLocalAsset =
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);

          const imageUrl = /^data:image/i.test(raw)
            ? raw
            : isLocalAsset
            ? `assets/img/categories/${raw}`
            : toPublicUrl(raw);

          const idVal = cat._id || cat.id || cat.CategoryId || cat.category_id;

          return {
            _id: String(idVal).trim(),
            name: cat.name || cat.CategoryName || cat.title || "Unnamed",
            imageUrl,
            isLocalAsset,
          };
        });

        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
          },
          ...normalized,
        ]);

        setDemo(false);
      } catch (e) {
        console.error("Failed to fetch categories from website:", e);

        setDemo(true);
        const normalized = mockPos.categories.map((cat) => {
          const raw = cat.image || "category-default.svg";
          const isLocalAsset =
            !/^data:image/i.test(raw) &&
            !isHttpUrl(raw) &&
            !looksLikeCatalog(raw);
          const imageUrl = /^data:image/i.test(raw)
            ? raw
            : isLocalAsset
            ? `assets/img/categories/${raw}`
            : toPublicUrl(raw);
          return {
            _id: String(cat.id).trim().toLowerCase(),
            name: cat.name,
            imageUrl,
            isLocalAsset,
          };
        });
        setCategories([
          {
            _id: "all",
            name: `${window.translate("All")}`,
            imageUrl: "assets/img/categories/category-01.svg",
            isLocalAsset: true,
          },
          ...normalized,
        ]);
      }
    };

    loadCategories();
  }, []);

  // fetch the product from website API, with category filter
  // fetch the products once, then filter client-side by category
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // We don't need activeTab here – fetch all products for the store
        const webProducts = await fetchWebProducts({
          page: 1,
          pagelimit: 100,
        });

        const normalizedProducts = webProducts.map((p) => {
          const rawImage = p.image || p.thumb || "product-default.jpg";

          const isLocalAsset =
            !/^data:image/i.test(rawImage) &&
            !isHttpUrl(rawImage) &&
            !looksLikeCatalog(rawImage);

          const imageUrl = /^data:image/i.test(rawImage)
            ? rawImage
            : isLocalAsset
            ? `assets/img/products/${rawImage}`
            : toPublicUrl(rawImage);

          // ---- category id + name mapping ----
          const primaryCatId =
            Array.isArray(p.categories) && p.categories.length
              ? String(p.categories[0]) // e.g. "214"
              : String(p.category_id || ""); // fallback

          const categoryName =
            categoryMap[primaryCatId] ||
            p.category_name ||
            p.category ||
            p.Category ||
            "";

          return {
            ...p,
            categories: p.categories || [], // keep original ids
            _id: String(p.product_id || p.id || p._id),
            Product_Name: p.name || p.Product_Name || "Unnamed",
            Category: categoryName, // 👈 nice name
            CategoryId: primaryCatId, // 👈 first category id
            image: imageUrl,
            Price: Number(p.price ?? p.Price ?? 0),
            Qty: Number(p.quantity ?? p.Qty ?? 0),
            quantity: 1,
          };
        });

        setProducts(normalizedProducts);
        setDemo(false);
      } catch (e) {
        console.error("Failed to load products from website:", e);
      }
    };

    // when categoryMap changes (after categories load), re-normalize products
    loadProducts();
  }, [categoryMap]);

  const filteredProducts = useMemo(() => {
    if (activeTab === "all") return products;
    const activeId = String(activeTab);

    return products.filter((p) => {
      if (Array.isArray(p.categories) && p.categories.length) {
        return p.categories.map(String).includes(activeId);
      }
      // fallback
      return String(p.CategoryId) === activeId;
    });
  }, [products, activeTab]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize) || 1;

  useEffect(() => {
    console.log(`📁 ActiveTab: ${activeTab}`);
    console.log("📦 Filtered Products:", filteredProducts);
  }, [activeTab, filteredProducts]);

  useEffect(() => {
    // whenever category (tab) changes, go back to first page
    setCurrentPage(1);
  }, [activeTab]);

  const handleProductClick = async (product) => {
    const productId = String(product._id || product.id || product.product_id);

    const alreadyExists = selectedItems.find(
      (item) => String(item._id) === productId
    );
    if (alreadyExists) return;

    try {
      let detailedProduct = productCache[productId];

      if (!detailedProduct) {
        if (demo) {
          detailedProduct = product;
        } else {
          detailedProduct = await fetchWebProductById(productId);
        }

        setProductCache((prev) => ({
          ...prev,
          [productId]: detailedProduct,
        }));
      }

      const enriched = {
        ...product,
        _id: productId,
        Product_Name:
          detailedProduct.Product_Name ||
          detailedProduct.name ||
          detailedProduct.meta_title ||
          detailedProduct.model ||
          (detailedProduct.sku ? `SKU ${detailedProduct.sku}` : null) ||
          product.Product_Name ||
          product.name ||
          "Unnamed",

        Price: Number(
          detailedProduct.Price ??
            detailedProduct.price ??
            product.Price ??
            product.price ??
            0
        ),

        quantity: 1,

        stockQty: Number(
          detailedProduct.Qty ??
            detailedProduct.quantity ??
            product.Qty ??
            product.quantity ??
            Infinity
        ),

        isVariant: Boolean(detailedProduct.isVariant ?? product.isVariant),
      };

      if (!demo && enriched.stockQty <= 0) {
        alert("This item is out of stock.");
        return;
      }

      setSelectedItems((prev) => [...prev, enriched]);
    } catch (error) {
      if (noStore403(error)) {
        setDemo(true);
        setSelectedItems((prev) => [
          ...prev,
          {
            ...product,
            _id: productId,
            Product_Name: product.Product_Name || product.name || "Unnamed",
            Price: Number(product.Price ?? product.price ?? 0),
            quantity: 1,
            stockQty: Number(product.Qty ?? product.quantity ?? Infinity),
          },
        ]);
        return;
      }
      console.error("Failed to fetch product details:", error);
    }
  };

  const handleQuantityChange = (id, newQty) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      const exists = prev.find((item) => String(item._id) === idStr);
      if (!exists) return prev;

      if (newQty <= 0) {
        return prev.filter((i) => String(i._id) !== idStr);
      }

      const max = Number(exists.stockQty ?? Infinity);
      if (newQty > max) {
        alert(`Only ${max} left in stock for "${exists.Product_Name}".`);
        newQty = max;
      }

      return prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: newQty } : item
      );
    });
  };

  useEffect(() => {
    console.log("🛒 Updated selectedItems:", selectedItems);
  }, [selectedItems]);

  const handleRemoveItem = (id) => {
    const idStr = String(id);

    setSelectedItems((prev) => {
      const newSelectedItems = prev.filter(
        (item) => String(item._id) !== idStr
      );

      if (newSelectedItems.length === 0) {
        console.log("🛒 Cart is empty!");
      }

      return newSelectedItems;
    });

    setProducts((prev) =>
      prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: 0 } : item
      )
    );
  };

  // tax details
  const subtotal = selectedItems.reduce(
    (sum, item) => sum + item.Price * item.quantity,
    0
  );
  const shipping = 0;

  const tax = selectedItems.reduce((sum, item) => {
    const taxVal = parseFloat(item.taxType) || 0;
    return sum + (taxVal / 100) * item.Price * item.quantity;
  }, 0);

  const coupon = 0;

  const discount = selectedItems.reduce((sum, item) => {
    const discountVal = parseFloat(item.discountValue) || 0;
    return sum + (discountVal / 100) * item.Price * item.quantity;
  }, 0);

  // const roundoff = 0.11;

  // const totalPayable = subtotal + shipping + tax - coupon - discount + roundoff;
  // const roundoff = 0.11; // ❌ removed

  const totalPayable = subtotal + shipping + tax - coupon - discount; // ✅ no roundoff

  useEffect(() => {
    setPayingAmount(Number(totalPayable || 0));
  }, [totalPayable]);

  //   Quantity change handler for CartCounter
  const QuantityCounter = ({ value, onChange, max = Infinity }) => {
    const handleDecrement = () => {
      if (value > 1) onChange(value - 1);
    };
    const handleIncrement = () => {
      if (value < max) onChange(value + 1);
    };
    const atMax = value >= max;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: "30px",
          overflow: "hidden",
          height: "36px",
          width: "100px",
          backgroundColor: "#fff",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleDecrement}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          −
        </button>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            userSelect: "none",
          }}
        >
          {value}
        </div>
        <button
          onClick={handleIncrement}
          disabled={atMax}
          title={atMax ? "No more stock" : ""}
          style={{
            width: "32px",
            height: "100%",
            border: "none",
            backgroundColor: "#f0f0f0",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>
    );
  };

  QuantityCounter.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
  };

  // Calculate change when receivedAmount or payingAmount changes
  useEffect(() => {
    const r = parseFloat(receivedAmount) || 0;
    const p = parseFloat(payingAmount) || 0;
    setChange(r - p > 0 ? r - p : 0);
  }, [receivedAmount, payingAmount]);

  // Handle payment type selection (before opening modal)
  const handlePaymentTypeSelect = (type) => {
    if (type === "cash") {
      setPaymentType({ value: "cash", label: "Cash" });
      setInvoiceNo(""); // Clear invoice number for cash
    } else if (type === "online") {
      setPaymentType({ value: "online", label: "Online" });
    }
  };

  // Handle Place Order submission - Updated to use Web Order API
  const handlePlaceOrder = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
        alert(window.translate("No items selected."));
        return;
      }

      const validItems = selectedItems.filter(
        (item) => item && item._id && item.quantity > 0
      );

      if (validItems.length === 0) {
        alert(window.translate("No valid items to process."));
        return;
      }

      // Validate invoice number for online payments
      if (paymentType?.value === "online" && !invoiceNo.trim()) {
        alert(
          window.translate("Please enter Invoice Number for online payment.")
        );
        return;
      }

      // Build order payload for OC API - Guest defaults applied automatically
      const orderPayload = buildOrderPayload({
        selectedItems: validItems,
        customerInfo: {
          firstname: customerInfo.firstname?.trim() || "Guest",
          lastname: customerInfo.lastname?.trim() || "Customer",
          email: customerInfo.email?.trim() || "guest@pos.local",
          telephone: customerInfo.telephone?.trim() || "00000000",
          company: customerInfo.company || "",
          address_1: customerInfo.address_1?.trim() || "POS Sale",
          address_2: customerInfo.address_2?.trim() || "POS Branch",
          city: customerInfo.city?.trim() || "Kuwait City",
          postcode: customerInfo.postcode?.trim() || "00000",
          country_id: customerInfo.country_id || 114,
          zone_id: customerInfo.zone_id || 1789,
          area_id: customerInfo.area_id || 153,
        },
        totalAmount: parseFloat(totalPayable),
        paymentType: paymentType?.value || "cash",
        invoiceNo: paymentType?.value === "online" ? invoiceNo : "",
        comment: saleNote || paymentNote || "",
      });

      console.log("📤 Sending order payload:", orderPayload);

      // Call the OC create order API
      const res = await createWebOrder(orderPayload);

      console.log("📥 Order API response:", res);

      // Check for success (adjust based on actual API response structure)
      if (res.success || res.order_id || res.data?.order_id) {
        const orderId =
          res.order_id || res.data?.order_id || `ORD-${Date.now()}`;

        // Create transaction object for invoice
        const tx = {
          orderId,
          type: paymentType?.label || "Cash",
          invoiceNo: paymentType?.value === "online" ? invoiceNo : "",
          receivedAmount: parseFloat(receivedAmount) || totalPayable,
          payingAmount: parseFloat(payingAmount) || totalPayable,
          change: parseFloat(change) || 0,
          paymentReceiver,
          totalAmount: parseFloat(totalPayable),
          createdAt: new Date().toISOString(),
          items: validItems.map((item) => ({
            productId: item._id,
            name: item.Product_Name || item.name,
            quantity: item.quantity,
            price: item.Price,
          })),
        };

        setLastTransaction(tx);
        alert(
          window.translate("✅ Order created successfully!") +
            ` Order ID: ${orderId}`
        );

        // Reset cart and forms
        setSelectedItems([]);
        setReceivedAmount("");
        setPayingAmount(0);
        setChange(0);
        setPaymentReceiver("");
        setPaymentNote("");
        setSaleNote("");
        setStaffNote("");
        setInvoiceNo("");
        setPaymentType({ value: "cash", label: "Cash" });
        setCustomerInfo({
          firstname: "",
          lastname: "",
          email: "",
          telephone: "",
          company: "",
          address_1: "",
          address_2: "",
          city: "Kuwait City",
          postcode: "",
          country_id: 114,
          zone_id: 1789,
          area_id: 153,
        });
      } else {
        throw new Error(res.message || res.error || "Order creation failed");
      }
    } catch (err) {
      console.error("❌ Order Error:", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create order";
      alert(window.translate("Failed to submit order: ") + errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render product card
  const renderProductCard = (product) => {
    const isSelected = selectedItems.some(
      (item) => String(item._id) === String(product._id)
    );

    return (
      <div
        key={product._id}
        className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3"
      >
        <div
          className={`product-info card mb-0 ${isSelected ? "active" : ""}`}
          data-product-id={product._id}
          onClick={() => handleProductClick(product)}
          tabIndex="0"
        >
          <Link to="#" className="pro-img">
            <img
              src={product.image}
              style={{ width: "160px", height: "160px", objectFit: "cover" }}
              alt={product.Product_Name}
              onError={(e) => (e.target.src = "/assets/img/products/shoes.jpg")}
            />
            <span>
              <i className="ti ti-circle-check-filled" />
            </span>
          </Link>

          <h6 className="cat-name">
            <Link to="#">{product.Category || "Uncategorized"}</Link>
          </h6>

          <h6 className="product-name">
            <Link to="#">{product.Product_Name || product.name}</Link>
          </h6>

          {product.allVariants?.length > 0 && (
            <div className="variant-buttons d-flex flex-wrap gap-1 mb-2 px-2">
              {product.allVariants.map((variant) => {
                const isVariantSelected = selectedItems.some(
                  (item) => String(item._id) === String(variant._id)
                );

                return (
                  <button
                    key={variant._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(variant);
                    }}
                    className={`btn btn-sm px-3 py-1 ${
                      isVariantSelected
                        ? "btn-primary text-white"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "capitalize",
                    }}
                  >
                    {variant.name}
                  </button>
                );
              })}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between price px-2">
            <p className="text-gray-9 mb-0">
              {Number(product.Price || 0).toFixed(2)}
            </p>
            <div className="qty-item m-0">
              <QuantityCounter
                value={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id)
                  )?.quantity ?? 1
                }
                max={
                  selectedItems.find(
                    (item) => String(item._id) === String(product._id)
                  )?.stockQty ?? Infinity
                }
                onChange={(newQty) => handleQuantityChange(product._id, newQty)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="main-wrapper pos-five">
      <div className="page-wrapper pos-pg-wrapper me-0 ms-0">
        <div className="content pos-design p-0">
          <div className="row pos-wrapper">
            {/* Products */}
            <div className="col-12 col-lg-7 col-xl-8 d-flex">
              <div className="pos-categories tabs_wrapper p-0 flex-fill">
                <div className="content-wrap">
                  <div className="tab-wrap mt-5">
                    <ul className="tabs owl-carousel pos-category5">
                      {categories.map((cat) => (
                        <li
                          key={cat._id}
                          onClick={() => {
                            console.log(`🟡 Category Selected: ${cat._id}`);
                            setActiveTab(cat._id);
                          }}
                          className={activeTab === cat._id ? "active" : ""}
                        >
                          <Link to="#">
                            {cat.isLocalAsset ? (
                              <ImageWithBasePath
                                src={cat.imageUrl}
                                alt="Category"
                              />
                            ) : (
                              <img
                                src={cat.imageUrl}
                                alt="Category"
                                onError={(e) =>
                                  (e.currentTarget.src =
                                    "/assets/img/categories/default.svg")
                                }
                              />
                            )}
                          </Link>
                          <h6>
                            <Link to="#">{cat.name}</Link>
                          </h6>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="tab-content-wrap">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 mt-5">
                      <div className="mb-3">
                        <h5 className="mb-1">{window.translate("Welcome")}</h5>
                        <p>
                          {new Date().toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="pos-products">
                      <div className="tabs_container">
                        <div
                          className={`tab_content ${activeTab} active`}
                          data-tab={activeTab}
                        >
                          <div className="row g-3">
                            {filteredProducts.length > 0 ? (
                              paginatedProducts.map(renderProductCard)
                            ) : (
                              <div className="col-12">
                                <p className="text-muted fs-3">
                                  {window.translate(
                                    "No products found in this category"
                                  )}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Pagination */}
                          {filteredProducts.length > 0 && totalPages > 1 && (
                            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pos-pagination">
                              <div className="text-muted small mb-2 mb-md-0">
                                {(() => {
                                  const start =
                                    (currentPage - 1) * pageSize + 1;
                                  const end = Math.min(
                                    currentPage * pageSize,
                                    filteredProducts.length
                                  );
                                  return (
                                    window.translate("Showing") +
                                    ` ${start}-${end} ${window.translate(
                                      "of"
                                    )} ${filteredProducts.length}`
                                  );
                                })()}
                              </div>

                              <nav>
                                <ul className="pagination mb-0">
                                  <li
                                    className={`page-item ${
                                      currentPage === 1 ? "disabled" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() =>
                                        currentPage > 1 &&
                                        setCurrentPage(currentPage - 1)
                                      }
                                    >
                                      «
                                    </button>
                                  </li>

                                  {Array.from(
                                    { length: totalPages },
                                    (_, i) => i + 1
                                  )
                                    .filter((page) => {
                                      // show current, first, last and +/-2 around current
                                      if (page === 1 || page === totalPages)
                                        return true;
                                      return Math.abs(page - currentPage) <= 2;
                                    })
                                    .map((page, idx, pages) => {
                                      const prevPage = pages[idx - 1];
                                      const showEllipsis =
                                        prevPage && page - prevPage > 1;

                                      return (
                                        <React.Fragment key={page}>
                                          {showEllipsis && (
                                            <li className="page-item disabled">
                                              <span className="page-link">
                                                …
                                              </span>
                                            </li>
                                          )}
                                          <li
                                            className={`page-item ${
                                              page === currentPage
                                                ? "active"
                                                : ""
                                            }`}
                                          >
                                            <button
                                              className="page-link"
                                              onClick={() =>
                                                setCurrentPage(page)
                                              }
                                            >
                                              {page}
                                            </button>
                                          </li>
                                        </React.Fragment>
                                      );
                                    })}

                                  <li
                                    className={`page-item ${
                                      currentPage === totalPages
                                        ? "disabled"
                                        : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() =>
                                        currentPage < totalPages &&
                                        setCurrentPage(currentPage + 1)
                                      }
                                    >
                                      »
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Products */}

            {/* Order Details */}
            <div className="col-12 col-lg-5 col-xl-4 ps-0 d-lg-flex">
              <aside className="product-order-list bg-secondary-transparent flex-fill w-100">
                <div className="card">
                  <div className="card-body">
                    <div className="order-head d-flex align-items-center justify-content-between w-100">
                      <div>
                        <h3>{window.translate("Order List")}</h3>
                      </div>
                    </div>

                    {/* Customer Info Section */}
                    <div className="customer-info block-section mt-3">
                      <h5 className="mb-2">
                        {window.translate("Customer Information")}
                      </h5>
                      <div className="row g-2">
                        <div className="col-6">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={window.translate("First Name")}
                            value={customerInfo.firstname}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                firstname: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Last Name")}
                            value={customerInfo.lastname}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                lastname: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="tel"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Phone")}
                            value={customerInfo.telephone}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                telephone: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-6">
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            placeholder={window.translate("Email")}
                            value={customerInfo.email}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="product-added block-section">
                      <div className="head-text d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="me-2">
                            {window.translate("Order Details")}
                          </h5>
                          <div className="badge bg-light text-gray-9 fs-12 fw-semibold py-2 border rounded">
                            {window.translate("Items")} :{" "}
                            <span className="text-teal">
                              {selectedItems.length}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="product-wrap">
                        <div className="empty-cart">
                          <div className="fs-24 mb-1">
                            <i className="ti ti-shopping-cart" />
                          </div>
                          <p className="fw-bold">
                            {window.translate("No Products Selected")}
                          </p>
                        </div>
                        <div className="product-list border-0 p-0">
                          <div className="table-responsive">
                            <table className="table table-borderless">
                              <thead>
                                <tr>
                                  <th className="fw-bold bg-light">
                                    {window.translate("Item")}
                                  </th>
                                  <th className="fw-bold bg-light">
                                    {window.translate("QTY")}
                                  </th>
                                  <th className="fw-bold bg-light text-end">
                                    {window.translate("Cost")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedItems.map((item) => (
                                  <tr key={`${item._id}-${item.quantity}`}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <Link
                                          className="delete-icon"
                                          to="#"
                                          onClick={() =>
                                            handleRemoveItem(item._id)
                                          }
                                        >
                                          <i className="ti ti-trash-x-filled" />
                                        </Link>
                                        <h6 className="fs-13 fw-normal">
                                          <Link to="#" className="link-default">
                                            {item.Product_Name || item.name}
                                          </Link>
                                        </h6>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="qty-item m-0">
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            border: "1px solid #ccc",
                                            borderRadius: "30px",
                                            overflow: "hidden",
                                            height: "36px",
                                            width: "100px",
                                            backgroundColor: "#fff",
                                          }}
                                        >
                                          <button
                                            onClick={() => {
                                              if (item.quantity > 1) {
                                                handleQuantityChange(
                                                  item._id,
                                                  item.quantity - 1
                                                );
                                              }
                                            }}
                                            style={{
                                              width: "32px",
                                              height: "100%",
                                              border: "none",
                                              backgroundColor: "#f0f0f0",
                                              fontSize: "18px",
                                              fontWeight: "bold",
                                              cursor: "pointer",
                                            }}
                                          >
                                            −
                                          </button>
                                          <div
                                            style={{
                                              flex: 1,
                                              textAlign: "center",
                                              fontSize: "16px",
                                              fontWeight: "500",
                                              userSelect: "none",
                                            }}
                                          >
                                            {item.quantity}
                                          </div>
                                          <button
                                            onClick={() => {
                                              handleQuantityChange(
                                                item._id,
                                                item.quantity + 1
                                              );
                                            }}
                                            style={{
                                              width: "32px",
                                              height: "100%",
                                              border: "none",
                                              backgroundColor: "#f0f0f0",
                                              fontSize: "18px",
                                              fontWeight: "bold",
                                              cursor: "pointer",
                                            }}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="fs-13 fw-semibold text-gray-9 text-end">
                                      {(item.Price * item.quantity).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="order-total bg-total bg-white p-0">
                      <h5 className="mb-3">
                        {window.translate("Payment Summary")}
                      </h5>
                      {selectedItems.length > 0 && (
                        <table className="table table-responsive table-borderless">
                          <tbody>
                            <tr>
                              <td>{window.translate("Tax")}</td>
                              <td className="text-gray-9 text-end">
                                {tax.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <span className="text-danger">
                                  {window.translate("Discount")}
                                </span>
                              </td>
                              <td className="text-danger text-end">
                                {discount.toFixed(2)}
                              </td>
                            </tr>
                            {/* <tr>
                              <td>
                                <div className="form-check form-switch p-0">
                                  <label
                                    className="form-check-label"
                                    htmlFor="round"
                                  >
                                    {window.translate("Roundoff")}
                                  </label>
                                </div>
                              </td>
                              <td className="text-gray-9 text-end">
                                +{roundoff.toFixed(2)}
                              </td>
                            </tr> */}
                            <tr>
                              <td>{window.translate("Sub Total")}</td>
                              <td className="text-gray-9 text-end">
                                {subtotal.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold border-top border-dashed">
                                {window.translate("Total Payable")}
                              </td>
                              <td className="text-gray-9 fw-bold text-end border-top border-dashed">
                                {totalPayable.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="card payment-method">
                  <div className="card-body">
                    <h5 className="mb-3">
                      {window.translate("Select Payment")}
                    </h5>
                    <div className="row align-items-center methods g-2">
                      {/* Cash Payment Option - Opens Modal */}
                      <div className="col-sm-6 col-md-6 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "cash"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          data-bs-toggle="modal"
                          data-bs-target="#payment-cash"
                          onClick={() => handlePaymentTypeSelect("cash")}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "cash"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-cash me-2 fs-5 ${
                              paymentType?.value === "cash"
                                ? "text-primary"
                                : "text-success"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("Cash")}
                          </p>
                        </Link>
                      </div>

                      {/* Online Payment Option - NO Modal, just select */}
                      <div className="col-sm-6 col-md-6 d-flex">
                        <Link
                          to="#"
                          className={`payment-item d-flex align-items-center justify-content-center p-2 flex-fill ${
                            paymentType?.value === "online"
                              ? "border-primary bg-primary-light"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePaymentTypeSelect("online");
                          }}
                          style={{
                            borderRadius: "8px",
                            border:
                              paymentType?.value === "online"
                                ? "2px solid #0d6efd"
                                : "1px solid #dee2e6",
                          }}
                        >
                          <i
                            className={`ti ti-credit-card me-2 fs-5 ${
                              paymentType?.value === "online"
                                ? "text-primary"
                                : "text-info"
                            }`}
                          />
                          <p className="fs-14 fw-medium mb-0">
                            {window.translate("Online")}
                          </p>
                        </Link>
                      </div>
                    </div>

                    {/* Invoice Number Field - Only shown for Online Payment */}
                    {paymentType?.value === "online" && (
                      <div className="mt-3">
                        <label className="form-label fw-semibold">
                          {window.translate("Invoice Number")}{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="ti ti-file-invoice" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={invoiceNo}
                            onChange={(e) => setInvoiceNo(e.target.value)}
                            placeholder={window.translate(
                              "Enter Transaction/Reference Number"
                            )}
                            required
                          />
                        </div>
                        <small className="text-muted">
                          {window.translate(
                            "Enter the transaction reference from online payment"
                          )}
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="btn-row d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center flex-fill gap-2">
                    <select
                      className="form-select pd-3"
                      style={{
                        maxWidth: 150,
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                      value={invoiceLang}
                      onChange={(e) => setInvoiceLang(e.target.value)}
                    >
                      <option value="en">
                        {window.translate("English Invoice")}
                      </option>
                      <option value="ar">
                        {window.translate("Arabic Invoice")}
                      </option>
                    </select>

                    <button
                      type="button"
                      className="btn btn-white d-flex align-items-center justify-content-center flex-fill m-0"
                      onClick={() => printInvoice(invoiceLang)}
                      title={window.translate("Print last successful invoice")}
                    >
                      <i className="ti ti-printer me-2 ms-2" />
                      {window.translate("Print Order")}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary d-flex align-items-center justify-content-center flex-fill m-0"
                    onClick={handlePlaceOrder}
                    disabled={isSubmitting || selectedItems.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {window.translate("Processing...")}
                      </>
                    ) : (
                      <>
                        <i className="ti ti-shopping-cart me-2 ms-2" />
                        {window.translate("Place Order")}
                      </>
                    )}
                  </button>
                </div>
              </aside>
            </div>
            {/* /Order Details */}
          </div>
        </div>
      </div>
      <PosModals
        selectedItems={selectedItems}
        totalAmount={totalPayable}
        receivedAmount={receivedAmount}
        setReceivedAmount={setReceivedAmount}
        payingAmount={payingAmount}
        setPayingAmount={setPayingAmount}
        change={change}
        setChange={setChange}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        paymentReceiver={paymentReceiver}
        setPaymentReceiver={setPaymentReceiver}
        paymentNote={paymentNote}
        setPaymentNote={setPaymentNote}
        saleNote={saleNote}
        setSaleNote={setSaleNote}
        staffNote={staffNote}
        setStaffNote={setStaffNote}
        handlePlaceOrder={handlePlaceOrder}
        // New props for invoice number
        invoiceNo={invoiceNo}
        setInvoiceNo={setInvoiceNo}
      />
    </div>
  );
};

export default Pos;
